import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { FilterModalTypes } from '../../components/FilterModal';
import { TableHeadItemOrderBy } from '../../components/TableHead';
import { useFilterTransformer } from '../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { getReportGeneratorOrderByOptions } from '../../pages/ReportGenerator/constants/table';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { useReportGeneratorActions } from './actions';
import { reportGeneratorFiltersAtom, reportGeneratorOrderByModalAtom } from './filters';
import { reportGeneratorListQuery } from './selector';
import { ReportGeneratorListItemNormalized } from './types';

export interface ReportGeneratorListingState {
  items: ReportGeneratorListItemNormalized[];
  loading: boolean;
  error?: Error;
  orderBy?: TableHeadItemOrderBy;
  isLoadingMore: boolean;
  appliedFilters: FilterModalTypes.FilterAtom['appliedFilters'];
  onRefresh: () => void;
  onEndReached: () => void;
  onOrderBy: (value: string) => void;
  onDownload: (item: ReportGeneratorListItemNormalized, onComplete: () => void) => void;
  onRetry: (item: ReportGeneratorListItemNormalized, onComplete: () => void) => void;
}

export const useReportGeneratorListing = (): ReportGeneratorListingState => {
  const [items, setItems] = useState<ReportGeneratorListItemNormalized[]>([]);

  const {
    page,
    randomId,
    requestNextPage,
    resetPage,
    filtersToApply,
    setFiltersToApply,
    getReportingFileAndDownload,
    retryReportingFile,
  } = useReportGeneratorActions();

  const advisorSelected = useRecoilValue(advisorIdToApisSelector);
  const partnerSelected = useRecoilValue(partnerIdToApisSelector);
  const [{ appliedOrderBy }, setOrderByAtom] = useRecoilState(reportGeneratorOrderByModalAtom);
  const { appliedFilters, availableFilters } = useRecoilValue(reportGeneratorFiltersAtom);
  const { transformFilters } = useFilterTransformer(availableFilters);

  useEffect(() => {
    const next = transformFilters(appliedFilters, {
      transformDateFilter: false,
      transformRangeFieldNames: true,
    });

    const categoryFilter = appliedFilters.find(
      (filter) => filter.fieldName === 'categoryId'
    )?.filter;
    if (Array.isArray(categoryFilter) && categoryFilter.length > 0) {
      next.categoryId = Number(categoryFilter[0]);
    }

    const documentTypeFilter = appliedFilters.find(
      (filter) => filter.fieldName === 'documentTypeId'
    )?.filter;
    if (documentTypeFilter && typeof documentTypeFilter === 'string') {
      next.documentTypeId = Number(documentTypeFilter);
    }

    setFiltersToApply(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const orderBy: TableHeadItemOrderBy | undefined = appliedOrderBy?.value
    ? { value: appliedOrderBy.value, direction: appliedOrderBy.direction }
    : undefined;

  const availableOrderByOptions = useMemo(() => getReportGeneratorOrderByOptions(), []);

  const { data, loading, error, refresher } = useRecoilQuery(
    reportGeneratorListQuery({
      page,
      filtersToApply,
      orderBy,
      randomId,
      advisorSelected,
      partnerSelected,
    })
  );

  const handleEndReached = () => {
    if (!data?.meta.pagination.hasNext || loading || error) {
      return;
    }

    requestNextPage();
  };

  const handleRefreshList = () => {
    resetPage();
    refresher();
  };

  const handleOrderBy = (value: string) => {
    const isSameCurrent = orderBy?.value === value;
    const direction = !isSameCurrent ? 'asc' : orderBy?.direction === 'asc' ? 'desc' : 'asc';
    setOrderByAtom((prev) => ({ ...prev, appliedOrderBy: { value, direction } }));
  };

  const handleDownload = async (
    item: ReportGeneratorListItemNormalized,
    onComplete: () => void
  ) => {
    if (!item.isDownloadEnabled) {
      return;
    }

    await getReportingFileAndDownload(item);
    onComplete();
  };

  const handleRetry = async (item: ReportGeneratorListItemNormalized, onComplete: () => void) => {
    if (!item.isAbleToRetry) {
      return;
    }

    await retryReportingFile(item);
    onComplete();
  };

  useEffect(() => {
    if (!data?.data) {
      return;
    }

    if (page === 1) {
      setItems(data.data);
      return;
    }

    setItems((prev) => [...prev, ...data.data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  useEffect(() => {
    setOrderByAtom((prev) => ({ ...prev, availableOrderBy: availableOrderByOptions }));
  }, [availableOrderByOptions, setOrderByAtom]);

  useEffect(() => {
    setItems([]);
  }, [
    appliedOrderBy?.value,
    appliedOrderBy?.direction,
    appliedFilters,
    advisorSelected,
    partnerSelected,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters,
    appliedOrderBy?.value,
    appliedOrderBy?.direction,
    advisorSelected,
    partnerSelected,
  ]);

  return {
    items,
    loading,
    error,
    orderBy,
    isLoadingMore: loading && items.length > 0 && page > 1,
    appliedFilters,
    onRefresh: handleRefreshList,
    onEndReached: handleEndReached,
    onOrderBy: handleOrderBy,
    onDownload: handleDownload,
    onRetry: handleRetry,
  };
};
